/**
 * Полнотекстовый поиск по whitelist документов `/docs` (docs/user + docs/release).
 * Индекс строится на клиенте один раз; морфология — лёгкий русский стемминг без зависимостей.
 */
import { keyboardLayoutSearchVariants } from '@domcrm/ui';
import { DOCS_RELEASE_PAGES, type DocsReleaseGroup } from './docs-release-inventory';
import { slugifyMarkdownHeading } from '../../shared/lib/markdown-agreement-html';

export type DocsSearchSnippetPart = { text: string; match: boolean };

export type DocsSearchSection = {
  docId: string;
  docTitle: string;
  docGroup: DocsReleaseGroup;
  anchor: string | null;
  heading: string | null;
  plainText: string;
  position: number;
};

export type DocsSearchResult = DocsSearchSection & {
  snippetParts: DocsSearchSnippetPart[];
  score: number;
};

type IndexedSection = DocsSearchSection & {
  titleStems: Set<string>;
  headingStems: Set<string>;
  bodyStems: Set<string>;
  words: { text: string; start: number; stem: string }[];
};

/** Окончания отсортированы от длинных к коротким; стем не короче 3 символов. */
const RU_SUFFIXES = [
  'иями', 'ями', 'ами', 'ией', 'иях', 'ием', 'иям', 'ыми', 'ими', 'ому', 'ему', 'ого', 'его',
  'ую', 'юю', 'ию', 'ия', 'ие', 'ий', 'ый', 'ой', 'ая', 'ое', 'ые', 'ах', 'ям', 'ам', 'ом', 'ем',
  'ов', 'ев', 'ей', 'ею', 'ою', 'их', 'ых', 'им', 'ым', 'ит', 'ут', 'ют', 'ат', 'ят', 'ил', 'ал',
  'ял', 'ела', 'ала', 'или', 'ели', 'ешь', 'ете', 'ть', 'ся', 'сь', 'а', 'я', 'ы', 'и', 'у', 'ю', 'о', 'е', 'ь'
];

export function normalizeSearchText(text: string): string {
  return text.toLowerCase().replace(/ё/g, 'е');
}

/** Стем слова для поиска: нижний регистр, ё→е, отсечение частого русского окончания. */
export function stemSearchToken(word: string): string {
  let w = normalizeSearchText(word).replace(/[^\p{L}\p{N}]/gu, '');
  if (w.length <= 3) return w;
  for (const suffix of RU_SUFFIXES) {
    if (w.length - suffix.length >= 3 && w.endsWith(suffix)) {
      w = w.slice(0, -suffix.length);
      break;
    }
  }
  return w;
}

function tokenizeWithPositions(text: string): IndexedSection['words'] {
  const words: IndexedSection['words'] = [];
  const re = /[\p{L}\p{N}]+/gu;
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    words.push({ text: match[0], start: match.index, stem: stemSearchToken(match[0]) });
  }
  return words;
}

function markdownToPlainText(markdown: string): string {
  return markdown
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`([^`]*)`/g, '$1')
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/^\s{0,3}#{1,6}\s+/gm, '')
    .replace(/^\s*>\s?/gm, '')
    .replace(/^\s*[-*+]\s+/gm, '')
    .replace(/^\s*\d+\.\s+/gm, '')
    .replace(/\|/g, ' ')
    .replace(/[*_~]/g, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

type RawSection = { heading: string | null; lines: string[] };

function splitMarkdownSections(markdown: string): RawSection[] {
  const lines = markdown.split('\n');
  const sections: RawSection[] = [];
  let current: RawSection = { heading: null, lines: [] };
  for (const line of lines) {
    const match = /^(#{1,3})\s+(.+?)\s*$/.exec(line);
    if (match && match[1].length >= 2) {
      if (current.heading !== null || current.lines.some((l) => l.trim()) ) sections.push(current);
      const heading = (match[2] ?? '').replace(/\s+#*$/, '').trim();
      current = { heading, lines: [] };
      continue;
    }
    if (match && match[1].length === 1) continue;
    current.lines.push(line);
  }
  if (current.heading !== null || current.lines.some((l) => l.trim())) sections.push(current);
  return sections;
}

function stemsOf(words: IndexedSection['words']): Set<string> {
  return new Set(words.map((w) => w.stem).filter((s) => s.length > 0));
}

function buildIndex(): IndexedSection[] {
  const sections: IndexedSection[] = [];
  let position = 0;
  for (const page of DOCS_RELEASE_PAGES) {
    const titleStems = new Set(tokenizeWithPositions(page.title).map((w) => w.stem));
    for (const raw of splitMarkdownSections(page.markdown)) {
      const plainText = markdownToPlainText(raw.lines.join('\n'));
      const headingWords = raw.heading ? tokenizeWithPositions(raw.heading) : [];
      const bodyWords = tokenizeWithPositions(plainText);
      sections.push({
        docId: page.id,
        docTitle: page.title,
        docGroup: page.group,
        anchor: raw.heading ? slugifyMarkdownHeading(raw.heading) : null,
        heading: raw.heading,
        plainText,
        position,
        titleStems,
        headingStems: stemsOf(headingWords),
        bodyStems: stemsOf(bodyWords),
        words: [...headingWords, ...bodyWords]
      });
      position += 1;
    }
  }
  return sections;
}

let cachedIndex: IndexedSection[] | null = null;

function getIndex(): IndexedSection[] {
  if (!cachedIndex) cachedIndex = buildIndex();
  return cachedIndex;
}

/** Все секции документов (для палитры Ctrl+K и тестов). */
export function listDocsSearchSections(): DocsSearchSection[] {
  return getIndex().map((s) => ({
    docId: s.docId,
    docTitle: s.docTitle,
    docGroup: s.docGroup,
    anchor: s.anchor,
    heading: s.heading,
    plainText: s.plainText,
    position: s.position
  }));
}

function stemsMatch(queryStem: string, tokenStem: string): boolean {
  if (!queryStem || !tokenStem) return false;
  if (queryStem === tokenStem) return true;
  if (queryStem.length >= 4 && tokenStem.startsWith(queryStem)) return true;
  if (tokenStem.length >= 4 && queryStem.startsWith(tokenStem)) return true;
  return false;
}

function setHasStem(set: Set<string>, queryStem: string): boolean {
  for (const stem of set) {
    if (stemsMatch(queryStem, stem)) return true;
  }
  return false;
}

function queryStems(rawQuery: string): string[] {
  const tokens = normalizeSearchText(rawQuery).split(/[^\p{L}\p{N}]+/u);
  const stems = tokens.map(stemSearchToken).filter((s) => s.length >= 2);
  return Array.from(new Set(stems));
}

function buildSnippet(section: IndexedSection, stems: string[]): DocsSearchSnippetPart[] {
  const text = section.plainText;
  if (!text) return [];
  const matched = section.words.filter((w) => stems.some((q) => stemsMatch(q, w.stem)));
  const first = matched[0];
  if (!first) return [{ text: text.slice(0, 160), match: false }];

  const windowStart = Math.max(0, first.start - 60);
  const windowEnd = Math.min(text.length, first.start + 140);
  let snippet = text.slice(windowStart, windowEnd);
  if (windowStart > 0) {
    const space = snippet.indexOf(' ');
    if (space > 0 && space < 30) snippet = snippet.slice(space + 1);
    snippet = `… ${snippet}`;
  }
  if (windowEnd < text.length) {
    const space = snippet.lastIndexOf(' ');
    if (space > snippet.length - 30) snippet = snippet.slice(0, space);
    snippet = `${snippet} …`;
  }

  const parts: DocsSearchSnippetPart[] = [];
  const re = /[\p{L}\p{N}]+/gu;
  let last = 0;
  let match: RegExpExecArray | null;
  while ((match = re.exec(snippet)) !== null) {
    const stem = stemSearchToken(match[0]);
    const isMatch = stems.some((q) => stemsMatch(q, stem));
    if (match.index > last) parts.push({ text: snippet.slice(last, match.index), match: false });
    parts.push({ text: match[0], match: isMatch });
    last = match.index + match[0].length;
  }
  if (last < snippet.length) parts.push({ text: snippet.slice(last), match: false });
  return parts;
}

/** Поиск секций по одному варианту запроса: все слова должны найтись в секции. */
function searchDocsReleaseForQuery(rawQuery: string): DocsSearchResult[] {
  const stems = queryStems(rawQuery);
  if (stems.length === 0) return [];

  const results: DocsSearchResult[] = [];
  for (const section of getIndex()) {
    let score = 0;
    let matchesAll = true;
    for (const stem of stems) {
      if (setHasStem(section.titleStems, stem)) {
        score += 5;
      } else if (setHasStem(section.headingStems, stem)) {
        score += 3;
      } else if (setHasStem(section.bodyStems, stem)) {
        score += 1;
      } else {
        matchesAll = false;
        break;
      }
    }
    if (!matchesAll) continue;
    results.push({
      docId: section.docId,
      docTitle: section.docTitle,
      docGroup: section.docGroup,
      anchor: section.anchor,
      heading: section.heading,
      plainText: section.plainText,
      position: section.position,
      snippetParts: buildSnippet(section, stems),
      score
    });
  }
  return results;
}

/**
 * Поиск по документации: все слова запроса должны найтись в секции.
 * Понимает неверную раскладку клавиатуры («nfhbas» → «тарифы»).
 */
export function searchDocsRelease(rawQuery: string, limit = 30): DocsSearchResult[] {
  const byKey = new Map<string, DocsSearchResult>();
  for (const variant of keyboardLayoutSearchVariants(rawQuery)) {
    for (const result of searchDocsReleaseForQuery(variant)) {
      const key = `${result.docId}:${result.position}`;
      const prev = byKey.get(key);
      if (!prev || result.score > prev.score) byKey.set(key, result);
    }
  }

  const results = Array.from(byKey.values());
  results.sort((a, b) => (b.score - a.score) || (a.position - b.position));
  return results.slice(0, limit);
}
